import React, { useEffect } from 'react';
import { flowRight as compose } from 'lodash';
import { withRouter, useParams } from 'react-router-dom';

import CustomerReviewsHeader from 'components/consumer/customer-reviews-list/CustomerReviewHeader';
import CustomerReviewsList from 'components/consumer/customer-reviews-list';
import withSearchState from 'components/consumer/customer-reviews-page/withSearchState';
import { logError } from 'commons/logger';
import useSuspenseQuery from 'commons/useSuspenseQuery';
import { usePrevious } from 'react-use';
import {
  GET_PRODUCT_INFO_FOR_REVIEWS,
  GET_PRODUCT_REVIEWS,
} from '../customer-reviews-page/CustomerReviews/queries';

const CustomerReviewsListByProduct = ({
  config,
  sortSelectOption,
  match,
  sortBy,
  onSortChanged,
}) => {
  const { slug } = useParams();
  const { type, title, subTitle } = config;
  const prevSortSelectOption = usePrevious(sortSelectOption);

  const {
    data,
    loading,
    error,
    refetch,
    fetchMore,
    networkStatus,
  } = useSuspenseQuery(GET_PRODUCT_REVIEWS, {
      slug: slug ?? null,
      id: null,
      sortBy: 'date-desc',
      offset: 0,
      limit: 3,
  });

  const { data: productInfo, refetch: refetchProductInfo } = useSuspenseQuery(
    GET_PRODUCT_INFO_FOR_REVIEWS,
    {
      variables: {
        slug: slug ?? null,
        id: null,
        sortBy: 'date-desc',
        offset: 0,
        limit: 3,
      },
    }
  );

  const currentReviewsCount = data?.productReviews?.reviews?.length ?? 0;
  const totalReviewsCount = data?.productReviews?.totalReviews ?? 0;
  const averageScore = data?.productReviews?.averageRating ?? 0;

  useEffect(() => {
    if (`${prevSortSelectOption}` !== `${sortSelectOption}`) {
      // Search cleared
      refetch({
        variables: {
          id: null,
          slug: match?.params?.slug ?? null,
          sortBy,
          offset: 0,
          limit: 3,
        },
      }).then(async () => {
        try {
          // Temp Fix for refetch not using new variables
          await fetchMore({
            variables: {
              sortBy,
              slug: match?.params?.slug ?? null,
              id: null,
              offset: 0,
              limit: 3,
            },
            updateQuery: (prevResults, { fetchMoreResult }) => {
              if (!fetchMoreResult) return prevResults;
              return {
                ...prevResults,
                productReviews: {
                  ...prevResults?.productReviews,
                  reviews: [...fetchMoreResult.productReviews.reviews],
                },
              };
            },
          });
        } catch (err) {
          logError(err);
        }
      });
    }
  }, [
    data,
    fetchMore,
    match?.params?.slug,
    prevSortSelectOption,
    refetch,
    sortBy,
    sortSelectOption,
  ]);

  return !data?.productReviews?.reviews ? null : (
    <>
      <CustomerReviewsHeader
        title={title}
        subTitle={subTitle}
        type={type}
        averageScore={averageScore}
        totalReviewsCount={totalReviewsCount}
        refetch={refetchProductInfo}
        onSortChanged={onSortChanged}
        sortSelectOption={sortSelectOption}
        product={productInfo?.product}
      />

      <CustomerReviewsList
        config={{
          type,
          reviews: data?.productReviews?.reviews ?? null,
          refetch,
          networkStatus,
          loading,
          error,
          loadMore: async () => {
            try {
              await fetchMore({
                variables: {
                  offset: currentReviewsCount,
                  sortBy,
                  slug: match?.params?.slug ?? null,
                  id: null,
                  limit: 3,
                },
                updateQuery: (prevResults, { fetchMoreResult }) => {
                  if (!fetchMoreResult) return prevResults;
                  return {
                    ...prevResults,
                    productReviews: {
                      ...prevResults?.productReviews,
                      reviews: [
                        ...prevResults.productReviews.reviews,
                        ...fetchMoreResult.productReviews.reviews,
                      ],
                    },
                  };
                },
              });
            } catch (err) {
              logError(err);
            }
          },
          totalReviewsCount,
          averageScore,
        }}
      />
    </>
  );
};

export default compose(
  withRouter,
  withSearchState
)(CustomerReviewsListByProduct);
